import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import ClassSummaryDialog from '../components/ClassSummaryDialog';
import LiveTranscriptionPanel from '../components/LiveTranscriptionPanel';
import ClassFeedbackModal from '../components/ClassFeedbackModal';
import {
  Video,
  Calendar as CalendarIcon,
  Clock,
  Play,
  Users,
  ArrowRight,
  ArrowLeft,
  X,
  MonitorPlay,
  Microphone,
  Camera,
  Spinner,
  Trash,
  Plus,
  UploadSimple,
  FilmStrip,
  FolderOpen,
  Pencil,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import UploadProgress, { makeOnProgress } from '../components/UploadProgress';
import { chunkedUpload } from '../utils/chunkedUpload';
import RecordingEditModal from '../components/RecordingEditModal';
import { format, parseISO, isFuture } from 'date-fns';
import { de } from 'date-fns/locale';
import { scheduleBadgeText } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback guest URL only — the real URL (host vs guest, host carries a signed
// roomKey that gives moderator powers) is fetched from /api/classes/live-room
// based on the authenticated user's role. NEVER hardcode the host URL here.
const WHEREBY_GUEST_FALLBACK = "https://hablaacademy.whereby.com/hablaacademy5563e565-b0b3-434c-885b-7085b85aa41a";

// Convert common video URLs (YouTube, Vimeo) to proper embed URLs
const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    let m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
    m = url.match(/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
    m = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
    m = url.match(/youtube\.com\/live\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
    m = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
    if (m) return url;
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m && !url.includes('player.vimeo.com')) return `https://player.vimeo.com/video/${m[1]}`;
    return url;
  } catch { return url; }
};

const ClassesPage = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const isDe = lang === 'de';
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [viewMode, setViewMode] = useState('upcoming');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showLiveRoom, setShowLiveRoom] = useState(false);
  const [activeSection, setActiveSection] = useState('live');
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(true);
  const [showAddRec, setShowAddRec] = useState(false);
  const [uploadingRec, setUploadingRec] = useState(false);
  const [recPct, setRecPct] = useState(0);
  const [recLabel, setRecLabel] = useState('');
  const [newRec, setNewRec] = useState({ title: '', description: '', level: 'A2', levels: ['A2'], video_url: '', recorded_at: '', scheduled_at: '' });
  // Class recordings — edit + resend UX (mirrors MaterialsPage).
  const [editingRec, setEditingRec] = useState(null);
  const [groups, setGroups] = useState([]);
  const [resendRec, setResendRec] = useState(null); // { recording, selected: Set }
  const [resendBusy, setResendBusy] = useState(false);

  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [summaryClass, setSummaryClass] = useState(null);
  // Whereby room URL — fetched from the backend so the HOST url (with the
  // signed roomKey) is only ever sent to teachers/admin. Starts as `null` so
  // the iframe is never rendered with the guest URL first: teachers were
  // hitting Whereby's "request access" flow because the iframe loaded the
  // GUEST url before the /live-room fetch resolved and swapping the src
  // mid-flight kept them stuck in the knock queue.
  const [wherebyUrl, setWherebyUrl] = useState(null);
  const [isHost, setIsHost] = useState(false);
  // Post-class anonymous feedback modal. We open it automatically when a
  // student returns to /classes after having joined the live room earlier
  // in this session (we set sessionStorage on join). Teachers don't get
  // prompted — only students should be giving the feedback.
  const [showFeedback, setShowFeedback] = useState(false);
  useEffect(() => {
    if (isTeacher) return;
    try {
      const joinedAt = parseInt(sessionStorage.getItem('habla.last_class_join') || '0', 10);
      const alreadyAsked = sessionStorage.getItem('habla.feedback_prompted') === '1';
      // Show 3 s after the student lands back on /classes (they likely came
      // from closing the Whereby tab). 8-hour window to catch slow returns.
      if (joinedAt && Date.now() - joinedAt < 8 * 60 * 60 * 1000 && !alreadyAsked) {
        const t = setTimeout(() => setShowFeedback(true), 3000);
        return () => clearTimeout(t);
      }
    } catch { /* private mode */ }
  }, [isTeacher]);

  useEffect(() => { loadClasses(); loadRecordings(); }, [levelFilter, viewMode]);

  // Fetch the role-appropriate Whereby room URL once on mount (and whenever
  // the auth state flips). Teachers/admin get the moderator URL. Only after
  // the fetch resolves do we set wherebyUrl → this prevents the iframe from
  // loading the guest URL first and putting teachers into the knock queue.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/classes/live-room`, { withCredentials: true })
      .then(res => {
        if (cancelled) return;
        setWherebyUrl(res.data?.room_url || WHEREBY_GUEST_FALLBACK);
        setIsHost(!!res.data?.is_host);
      })
      .catch(() => {
        // Backend unreachable → still let students knock into the room, but
        // teachers will need to retry (the guest URL has no roomKey so they'd
        // land in the knock queue anyway).
        if (!cancelled) setWherebyUrl(WHEREBY_GUEST_FALLBACK);
      });
    return () => { cancelled = true; };
  }, [user?.user_id, user?.role]);

  const loadRecordings = async () => {
    try {
      let url = `${API}/recordings?limit=50`;
      if (levelFilter !== 'all') url += `&level=${levelFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setRecordings(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingRecordings(false); }
  };

  const createRecording = async () => {
    if (!newRec.title || !newRec.video_url) return toast.error('Título y video necesarios');
    try {
      // datetime-local is teacher's LOCAL time; convert to a UTC ISO so
      // the schedule works regardless of server / student timezone.
      const sched = (newRec.scheduled_at || '').trim();
      const body = {
        ...newRec,
        scheduled_at: sched ? new Date(sched).toISOString() : '',
      };
      await axios.post(`${API}/recordings`, body, { withCredentials: true });
      toast.success(sched ? 'Programada para la fecha indicada' : 'Clase grabada añadida');
      setShowAddRec(false);
      setNewRec({ title: '', description: '', level: 'A2', levels: ['A2'], video_url: '', recorded_at: '', scheduled_at: '' });
      loadRecordings();
    } catch (e) { toast.error('Error'); }
  };

  const deleteRecording = async (id) => {
    try {
      await axios.delete(`${API}/recordings/${id}`, { withCredentials: true });
      toast.success('Eliminada');
      loadRecordings();
    } catch (e) { toast.error('Error'); }
  };

  // Load class groups for the resend dialog. Teacher only — endpoint returns 403 otherwise.
  useEffect(() => {
    if (!isTeacher) return;
    axios.get(`${API}/groups`, { withCredentials: true })
      .then(res => setGroups(res.data || []))
      .catch(() => setGroups([]));
  }, [isTeacher]);

  const openResendRec = (rec) => setResendRec({ recording: rec, selected: new Set() });
  const toggleResendTarget = (gid) => {
    setResendRec(d => {
      if (!d) return d;
      const s = new Set(d.selected);
      if (s.has(gid)) s.delete(gid); else s.add(gid);
      return { ...d, selected: s };
    });
  };
  const submitResendRec = async () => {
    if (!resendRec) return;
    const targets = Array.from(resendRec.selected);
    if (targets.length === 0) return toast.error('Bitte mindestens eine Klasse auswählen');
    setResendBusy(true);
    try {
      await axios.post(`${API}/recordings/${resendRec.recording.recording_id}/resend`,
        { group_ids: targets }, { withCredentials: true });
      toast.success(`An ${targets.length} Klasse${targets.length === 1 ? '' : 'n'} zugewiesen`);
      setResendRec(null);
      loadRecordings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setResendBusy(false);
    }
  };

  const loadClasses = async () => {
    try {
      let url = `${API}/classes?limit=50`;
      if (levelFilter !== 'all') url += `&level=${levelFilter}`;
      if (viewMode === 'upcoming') url += '&upcoming=true';
      const res = await axios.get(url, { withCredentials: true });
      setClasses(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const joinClass = (cls) => {
    if (cls.whereby_room_url) {
      window.open(cls.whereby_room_url, '_blank');
    } else if (cls.video_url) {
      window.open(cls.video_url, '_blank');
    } else {
      toast.info('Kein Video-Link verfügbar');
    }
  };

  const getClassStatus = (cls) => {
    const scheduledAt = parseISO(cls.scheduled_at);
    if (cls.is_live) return 'live';
    if (isFuture(scheduledAt)) return 'upcoming';
    if (cls.video_url) return 'recorded';
    return 'past';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium animate-pulse">LIVE</span>;
      case 'upcoming':
        return <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">Geplant</span>;
      case 'recorded':
        return <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">Aufzeichnung</span>;
      default:
        return <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">Vergangen</span>;
    }
  };

  // Full-screen live room view
  if (showLiveRoom) {
    return (
      <div className="space-y-0" data-testid="live-room-view">
        {/* Top bar */}
        <div className="flex items-center justify-between bg-[#1a1a2e] rounded-t-2xl px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-white font-heading font-semibold text-lg">Habla — Live-Klassenzimmer</span>
            {isHost && (
              <span className="px-2.5 py-1 rounded-full bg-[#EAB308]/20 text-[#EAB308] text-xs font-bold tracking-wider uppercase border border-[#EAB308]/30" data-testid="host-badge">
                Host
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Camera size={16} /> <Microphone size={16} />
              <span className="ml-1">Kamera & Mikrofon erlaubt</span>
            </div>
            <Button
              onClick={() => setShowLiveRoom(false)}
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="leave-room-btn"
            >
              <ArrowLeft size={18} className="mr-2" />
              Zurück
            </Button>
          </div>
        </div>
        
        {/* Whereby iframe + Live Transcription panel side-by-side on desktop,
            stacked on mobile. The transcription captures the teacher's mic via
            Web Speech API and broadcasts ES + DE captions to every student. */}
        <div className="bg-[#0f0f23] rounded-b-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-3 p-3">
            {wherebyUrl ? (
              <iframe
                src={wherebyUrl}
                allow="camera; microphone; fullscreen; speaker; display-capture; compute-pressure"
                style={{ height: '75vh', width: '100%', border: 'none', borderRadius: '12px' }}
                title="Habla Live Classroom"
                data-testid="whereby-iframe"
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-xl bg-[#0a0a1f] border border-white/5"
                style={{ height: '75vh' }}
                data-testid="whereby-loading"
              >
                <div className="text-center">
                  <div className="inline-block w-10 h-10 border-4 border-[#AB1C24] border-t-transparent rounded-full animate-spin mb-3" />
                  <p className="text-white/60 text-sm">Klassenraum wird vorbereitet…</p>
                </div>
              </div>
            )}
            <div className="lg:max-h-[75vh]">
              <LiveTranscriptionPanel
                roomId="habla-main"
                isTeacher={isTeacher}
              />
            </div>
          </div>
        </div>

        {/* Info bar */}
        <div className="bg-[#1a1a2e] rounded-2xl mt-4 p-4 flex items-center justify-between">
          <p className="text-white/70 text-sm">
            Powered by Whereby — Kein Download nötig. Deine Kamera und Mikrofon werden im Raum aktiviert.
          </p>
          <Button
            onClick={() => setShowLiveRoom(false)}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="end-session-btn"
          >
            <X size={16} className="mr-2" />
            Sitzung beenden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            <Video size={28} weight="duotone" className="text-[#AB1C24]" />
            {isDe ? 'Videoanruf' : 'Videollamada'}
          </h1>
          <p className="text-[#57534E] mt-1">{isDe ? 'Live-Kurse und frühere Aufzeichnungen' : 'Clases en vivo y grabaciones anteriores'}</p>
        </div>
        <div className="flex bg-[#F5F5F4] rounded-xl p-1 gap-1" data-testid="classes-tabs">
          <button onClick={() => setActiveSection('live')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeSection === 'live' ? 'bg-white text-[#292524] shadow-sm' : 'text-[#78716C]'}`}
            data-testid="tab-live">Clases en vivo</button>
          <button onClick={() => setActiveSection('recordings')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeSection === 'recordings' ? 'bg-white text-[#292524] shadow-sm' : 'text-[#78716C]'}`}
            data-testid="tab-recordings">Clases grabadas</button>
        </div>
      </div>

      {activeSection === 'recordings' ? (
        /* ── CLASES GRABADAS ─────────────────────────────── */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Select value={levelFilter} onValueChange={v => { setLevelFilter(v); setLoadingRecordings(true); loadRecordings(); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Nivel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="A1">A1</SelectItem>
                <SelectItem value="A2">A2</SelectItem>
                <SelectItem value="B1">B1</SelectItem>
                <SelectItem value="B2">B2</SelectItem>
              </SelectContent>
            </Select>
            {isTeacher && (
              <Button onClick={() => setShowAddRec(!showAddRec)} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="add-recording-btn">
                <Plus size={16} className="mr-1" /> Añadir grabacion
              </Button>
            )}
          </div>

          {showAddRec && isTeacher && (
            <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6 space-y-4" data-testid="add-recording-form">
              <h3 className="font-heading font-semibold text-lg text-[#292524]">Nueva grabacion</h3>
              <Input placeholder="Título (ej. Clase 15.04 — Pretérito Indefinido)" value={newRec.title} onChange={e => setNewRec(p => ({...p, title: e.target.value}))} />
              <Input placeholder="Descripcion" value={newRec.description} onChange={e => setNewRec(p => ({...p, description: e.target.value}))} />
              <div className="flex gap-3 flex-wrap items-start">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-xs font-medium text-[#57534E] mb-1.5">Niveles (uno o varios)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['A1','A2','B1','B2','C1'].map(lvl => {
                      const on = (newRec.levels || []).includes(lvl);
                      return (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setNewRec(p => {
                            const cur = p.levels || [];
                            const has = cur.includes(lvl);
                            let next = has ? cur.filter(l => l !== lvl) : [...cur, lvl];
                            if (!next.length) next = [lvl];
                            return { ...p, levels: next, level: next[0] };
                          })}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                            on
                              ? 'bg-[#AB1C24] text-white border-[#AB1C24]'
                              : 'bg-white text-[#57534E] border-[#D6D3D1] hover:border-[#AB1C24] hover:text-[#AB1C24]'
                          }`}
                          data-testid={`rec-lvl-${lvl}`}
                        >
                          {lvl}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Input type="date" value={newRec.recorded_at} onChange={e => setNewRec(p => ({...p, recorded_at: e.target.value}))} className="w-[180px]" />
              </div>
              <Input placeholder="URL del video (YouTube, Vimeo...)" value={newRec.video_url} onChange={e => setNewRec(p => ({...p, video_url: e.target.value}))} />
              <div>
                <p className="text-xs font-medium text-[#57534E] mb-1.5">Programar publicación (opcional)</p>
                <Input
                  type="datetime-local"
                  value={newRec.scheduled_at || ''}
                  onChange={e => setNewRec(p => ({...p, scheduled_at: e.target.value}))}
                  data-testid="rec-form-scheduled-at"
                />
                <p className="text-[11px] text-[#78716C] mt-1 leading-snug">
                  Vacío = publicar ahora. Con fecha = los alumnos la verán a partir de ese momento.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F4] border border-[#D6D3D1] rounded-lg cursor-pointer hover:bg-gray-100 text-sm">
                  <UploadSimple size={16} />
                  {uploadingRec ? 'Subiendo...' : 'O subir archivo'}
                  <input type="file" className="hidden" accept="video/*,.mp4,.webm,.mov" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingRec(true);
                    setRecPct(0);
                    setRecLabel(file.name);
                    try {
                      const r = await chunkedUpload(file, 'content', setRecPct);
                      setNewRec(p => ({...p, video_url: `${API}/content/${r.storage_path}`}));
                      toast.success('Video subido');
                    } catch (err) {
                      const msg = err.response?.data?.detail || err.message || 'Error desconocido';
                      console.error('class upload error', err);
                      toast.error(`Error al subir: ${msg}`);
                    }
                    finally {
                      setUploadingRec(false);
                      setRecPct(0);
                      setRecLabel('');
                      e.target.value = '';
                    }
                  }} />
                </label>
              </div>
              <UploadProgress active={uploadingRec} percent={recPct} label={recLabel} />
              <div className="flex gap-3">
                <Button onClick={createRecording} className="bg-[#AB1C24] hover:bg-[#8A151C]">Guardar</Button>
                <Button variant="outline" onClick={() => setShowAddRec(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {loadingRecordings ? (
            <div className="flex justify-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>
          ) : recordings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recordings.map(rec => (
                <div key={rec.recording_id} className="bg-white rounded-xl border border-[#D6D3D1] overflow-hidden hover:shadow-md transition-shadow" data-testid={`rec-${rec.recording_id}`}>
                  <div className="relative bg-[#292524] aspect-video flex items-center justify-center">
                    {rec.video_url ? (
                      rec.video_url.includes('youtube') || rec.video_url.includes('youtu.be') || rec.video_url.includes('vimeo') ? (
                        <iframe key={rec.recording_id} src={toEmbedUrl(rec.video_url)} title={rec.title} className="w-full h-full border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
                      ) : (
                        <video key={rec.recording_id} src={rec.video_url} controls className="w-full h-full" preload="metadata" />
                      )
                    ) : (
                      <Video size={40} className="text-white/30" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex flex-wrap gap-1">
                        {(rec.levels && rec.levels.length ? rec.levels : [rec.level]).map(l => (
                          <span key={l} className={`level-badge ${l}`}>{l}</span>
                        ))}
                      </div>
                      {rec.recorded_at && <span className="text-xs text-[#78716C]">{new Date(rec.recorded_at).toLocaleDateString('de-DE')}</span>}
                      {isTeacher && rec.scheduled_at && new Date(rec.scheduled_at) > new Date() && (
                        <span
                          className="ml-auto text-[10px] font-bold tracking-wider uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          data-testid={`rec-scheduled-${rec.recording_id}`}
                          title={`Wird veröffentlicht: ${new Date(rec.scheduled_at).toLocaleString('de-DE')}`}
                        >
                          {scheduleBadgeText(rec.scheduled_at)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-[#292524] text-sm">{rec.title}</h3>
                    {rec.description && <p className="text-xs text-[#78716C] mt-1 line-clamp-2">{rec.description}</p>}
                    {isTeacher && (
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => setEditingRec(rec)}
                          className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                          data-testid={`rec-edit-${rec.recording_id}`}
                        >
                          <Pencil size={12} weight="bold" /> Bearbeiten
                        </button>
                        <button
                          onClick={() => openResendRec(rec)}
                          className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"
                          data-testid={`rec-resend-${rec.recording_id}`}
                        >
                          <PaperPlaneTilt size={12} weight="duotone" /> An Klassen zuweisen
                        </button>
                        <button
                          onClick={() => deleteRecording(rec.recording_id)}
                          className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                          data-testid={`rec-delete-${rec.recording_id}`}
                        >
                          <Trash size={12} /> Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FolderOpen size={56} className="mx-auto text-[#D6D3D1] mb-4" />
              <p className="text-[#78716C] text-lg">Todavia no hay grabaciones</p>
              <p className="text-sm text-[#A8A29E] mt-1">Aquí aparecerán las grabaciones de las clases anteriores</p>
            </div>
          )}
        </div>
      ) : (
      /* ── CLASES EN VIVO ──────────────────────────────── */
      <>

      {/* JOIN LIVE CLASSROOM - Hero Card */}
      <div
        className="relative overflow-hidden rounded-2xl border border-[#D6D3D1]"
        data-testid="live-classroom-card"
      >
        <div className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#0f3460] p-8 md:p-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/20">
                <MonitorPlay size={48} className="text-white" weight="duotone" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                Live-Klassenzimmer
              </h2>
              <p className="text-white/70 mb-1 max-w-xl">
                Trete dem interaktiven Live-Raum bei und lerne Spanisch direkt mit deinem Lehrer Alberto und anderen Teilnehmern.
              </p>
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm flex items-center gap-1.5">
                  <Camera size={14} /> Videoanruf
                </span>
                <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm flex items-center gap-1.5">
                  <Microphone size={14} /> Mikrofon
                </span>
                <span className="px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-sm flex items-center gap-1.5">
                  <Users size={14} /> Gruppenunterricht
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Button
                onClick={() => {
                  setShowLiveRoom(true);
                  // Track join time so we can prompt for anonymous feedback
                  // the next time the student lands back on /classes.
                  if (!isTeacher) {
                    try {
                      sessionStorage.setItem('habla.last_class_join', String(Date.now()));
                      sessionStorage.removeItem('habla.feedback_prompted');
                    } catch { /* private mode */ }
                  }
                }}
                className="bg-[#AB1C24] hover:bg-[#8A151C] text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-[#AB1C24]/30 transition-all hover:scale-105"
                data-testid="join-live-room-btn"
              >
                <Video size={24} className="mr-3" weight="fill" />
                Jetzt beitreten
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & View Modes */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4">
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="w-[150px]" data-testid="level-filter">
              <SelectValue placeholder="Niveau" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Niveaus</SelectItem>
              <SelectItem value="A1">A1</SelectItem>
              <SelectItem value="A2">A2</SelectItem>
              <SelectItem value="B1">B1</SelectItem>
              <SelectItem value="B2">B2</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 bg-[#F5F5F4] p-1 rounded-xl">
          <button
            onClick={() => setViewMode('upcoming')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'upcoming' ? 'bg-white text-[#AB1C24] shadow-sm' : 'text-[#78716C]'
            }`}
            data-testid="upcoming-btn"
          >
            Kommende
          </button>
          <button
            onClick={() => setViewMode('past')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'past' ? 'bg-white text-[#AB1C24] shadow-sm' : 'text-[#78716C]'
            }`}
            data-testid="past-btn"
          >
            Vergangene
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'calendar' ? 'bg-white text-[#AB1C24] shadow-sm' : 'text-[#78716C]'
            }`}
            data-testid="calendar-btn"
          >
            <CalendarIcon size={20} />
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            locale={de}
            className="mx-auto"
          />
        </div>
      )}

      {/* Scheduled Classes Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.length > 0 ? (
          classes.map((cls) => {
            const status = getClassStatus(cls);
            const scheduledAt = parseISO(cls.scheduled_at);
            return (
              <div key={cls.class_id} className="class-card" data-testid={`class-card-${cls.class_id}`}>
                <div className={`h-32 flex items-center justify-center ${
                  status === 'live' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                  status === 'upcoming' ? 'bg-gradient-to-br from-[#AB1C24] to-[#8A151C]' :
                  'bg-gradient-to-br from-[#5B8C9A] to-[#4A7A87]'
                }`}>
                  <Video size={48} className="text-white/80" weight="thin" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`level-badge ${cls.level}`}>{cls.level}</span>
                    {getStatusBadge(status)}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[#292524] mb-2">{cls.title}</h3>
                  <p className="text-[#57534E] text-sm mb-4 line-clamp-2">{cls.description}</p>
                  <div className="flex items-center gap-4 text-sm text-[#78716C] mb-4">
                    <div className="flex items-center gap-1">
                      <CalendarIcon size={16} />
                      <span>{format(scheduledAt, 'd. MMM', { locale: de })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={16} />
                      <span>{format(scheduledAt, 'HH:mm', { locale: de })} Uhr</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => joinClass(cls)}
                    className={`w-full ${
                      status === 'live' ? 'bg-red-500 hover:bg-red-600' :
                      status === 'recorded' ? 'bg-[#6B8E23] hover:bg-[#5a7a1e]' :
                      'bg-[#AB1C24] hover:bg-[#8A151C]'
                    }`}
                    disabled={status === 'past' && !cls.video_url}
                    data-testid={`join-class-${cls.class_id}`}
                  >
                    {status === 'live' ? (
                      <><Users size={20} className="mr-2" />Jetzt teilnehmen</>
                    ) : status === 'recorded' ? (
                      <><Play size={20} className="mr-2" />Aufzeichnung ansehen</>
                    ) : status === 'upcoming' ? (
                      <>Zum Kalender hinzufügen<ArrowRight size={20} className="ml-2" /></>
                    ) : (
                      'Nicht verfügbar'
                    )}
                  </Button>
                  {/* Teacher-only: generate pedagogical summary + email students. */}
                  {isTeacher && (
                    <button
                      onClick={() => setSummaryClass(cls)}
                      className="mt-2 w-full text-xs font-semibold text-[#AB1C24] hover:text-[#8A151C] inline-flex items-center justify-center gap-1.5"
                      data-testid={`class-summary-open-${cls.class_id}`}
                    >
                      ✨ Zusammenfassung erstellen &amp; versenden
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-full text-center py-12">
            <Video size={48} className="mx-auto text-[#78716C] mb-4" />
            <p className="text-[#78716C]">
              {viewMode === 'upcoming'
                ? 'Keine kommenden Kurse geplant'
                : 'Keine vergangenen Kurse gefunden'}
            </p>
          </div>
        )}
      </div>

      {/* Whereby Info */}
      <div className="bg-gradient-to-br from-[#5B8C9A]/10 to-[#6B8E23]/10 rounded-2xl p-6 border border-[#D6D3D1]">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <Video size={24} className="text-[#5B8C9A]" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-[#292524] mb-1">
              Live-Unterricht mit Whereby
            </h3>
            <p className="text-[#57534E]">
              Unsere Live-Kurse finden über Whereby statt. Du brauchst keine Software zu installieren -
              klicke einfach auf "Jetzt beitreten" und der Unterricht startet direkt in deinem Browser.
            </p>
          </div>
        </div>
      </div>

      </>
      )}

      {summaryClass && (
        <ClassSummaryDialog
          classId={summaryClass.class_id}
          classTitle={summaryClass.title}
          level={summaryClass.level}
          onClose={() => setSummaryClass(null)}
        />
      )}
      <ClassFeedbackModal
        open={showFeedback}
        isDe={lang !== 'es'}
        classDate={new Date().toISOString().slice(0, 10)}
        onClose={() => {
          setShowFeedback(false);
          try {
            sessionStorage.setItem('habla.feedback_prompted', '1');
            sessionStorage.removeItem('habla.last_class_join');
          } catch { /* noop */ }
        }}
      />

      {/* Teacher-only: edit + resend dialogs for class recordings */}
      <RecordingEditModal
        recording={editingRec}
        open={!!editingRec}
        onClose={() => setEditingRec(null)}
        onSaved={(updated) => {
          setRecordings(prev => prev.map(r => r.recording_id === updated.recording_id ? { ...r, ...updated } : r));
          setEditingRec(null);
        }}
      />

      {resendRec && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !resendBusy && setResendRec(null)} data-testid="rec-resend-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-[#292524]">An weitere Klassen zuweisen</h3>
                <p className="text-sm text-[#78716C] mt-1 truncate">{'"'}{resendRec.recording.title || '(ohne Titel)'}{'"'}</p>
              </div>
              <button onClick={() => !resendBusy && setResendRec(null)} className="p-1 rounded-lg hover:bg-stone-100"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => toggleResendTarget('__ALL__')}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendRec.selected.has('__ALL__') ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                data-testid="rec-resend-target-__ALL__"
              >
                <span className="font-semibold text-[#292524]">Alle Klassen</span>
                <span className="ml-2 text-xs text-[#78716C]">— Für jeden Schüler sichtbar</span>
              </button>
              {groups.map(g => (
                <button
                  key={g.group_id}
                  type="button"
                  onClick={() => toggleResendTarget(g.group_id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${resendRec.selected.has(g.group_id) ? 'border-[#AB1C24] bg-[#AB1C24]/5' : 'border-stone-200 hover:border-stone-400'}`}
                  data-testid={`rec-resend-target-${g.group_id}`}
                >
                  <span className="font-semibold text-[#292524]">{g.name}</span>
                  {g.level && <span className="ml-2 text-xs text-[#78716C]">· {g.level}</span>}
                </button>
              ))}
              {groups.length === 0 && (
                <p className="text-xs text-[#A8A29E] italic px-1">Keine Klassen definiert. Lege sie zuerst im Lehrer-Dashboard an.</p>
              )}
            </div>
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <Button variant="outline" onClick={() => setResendRec(null)} disabled={resendBusy} data-testid="rec-resend-cancel" className="flex-1">Abbrechen</Button>
              <Button
                onClick={submitResendRec}
                disabled={resendBusy || resendRec.selected.size === 0}
                className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white"
                data-testid="rec-resend-submit"
              >
                {resendBusy ? <Spinner size={16} className="animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" weight="fill" />}
                {resendBusy ? 'Sende…' : `An ${resendRec.selected.size} Klasse${resendRec.selected.size === 1 ? '' : 'n'} senden`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesPage;
